import OnboardingProgressModel from "../../../shared/models/onboarding/api/OnboardingProgressModel";

export type OnboardingPhase = "onboarding" | "testing";

export interface TestingStepFeedbackHistoryEntry {
    value: string;
    savedAt: string;
}

export interface TestingProgressState {
    currentStepId?: string;
    completedStepIds?: string[];
    startedAt?: string;
    completedAt?: string;
    stepFeedbackByStepId?: Record<string, string>;
    stepFeedbackHistoryByStepId?: Record<string, TestingStepFeedbackHistoryEntry[]>;
}

export interface OnboardingSnapshotState {
    currentStep?: string;
    percentComplete?: number;
    isComplete?: boolean;
}

export interface OnboardingProgressDetails {
    answers?: Record<string, unknown>;
    flowType?: string;
    lastSavedAt?: string;
    phase?: OnboardingPhase;
    testing?: TestingProgressState;
    onboardingSnapshot?: OnboardingSnapshotState;
    testingEnabledAt?: string;
    [key: string]: any;
}

export const parseOnboardingProgressDetails = (detailsJson?: string): OnboardingProgressDetails => {
    if (!detailsJson?.trim()) {
        return {};
    }

    try {
        const parsed = JSON.parse(detailsJson);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const serializeOnboardingProgressDetails = (details: OnboardingProgressDetails): string => {
    return JSON.stringify(details);
};

export const getOnboardingProgressPhase = (progress?: OnboardingProgressModel | null): OnboardingPhase => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    return details.phase === "testing" ? "testing" : "onboarding";
};

export const getTestingProgressState = (progress?: OnboardingProgressModel | null): TestingProgressState => {
    const details = parseOnboardingProgressDetails(progress?.detailsJson);
    return details.testing && typeof details.testing === "object" ? details.testing : {};
};

export const getTestingStepFeedback = (progress?: OnboardingProgressModel | null): Record<string, string> => {
    const testing = getTestingProgressState(progress);
    const feedback = testing.stepFeedbackByStepId;

    return feedback && typeof feedback === "object" && !Array.isArray(feedback) ? feedback : {};
};

export const getTestingStepFeedbackHistory = (progress?: OnboardingProgressModel | null): Record<string, TestingStepFeedbackHistoryEntry[]> => {
    const testing = getTestingProgressState(progress);
    const historyByStepId = testing.stepFeedbackHistoryByStepId;

    if (!historyByStepId || typeof historyByStepId !== "object" || Array.isArray(historyByStepId)) {
        return {};
    }

    return Object.keys(historyByStepId).reduce((accumulator, stepId) => {
        const entries = historyByStepId[stepId];
        if (!Array.isArray(entries)) {
            return accumulator;
        }

        const validEntries = entries.filter(entry => {
            return !!entry
                && typeof entry === "object"
                && typeof entry.value === "string"
                && typeof entry.savedAt === "string"
                && !!entry.savedAt.trim();
        });

        if (validEntries.length > 0) {
            accumulator[stepId] = validEntries;
        }

        return accumulator;
    }, {} as Record<string, TestingStepFeedbackHistoryEntry[]>);
};

export const getOnboardingProgressAnswers = (progress?: OnboardingProgressModel | null): Record<string, unknown> => {
    const parsedDetails = parseOnboardingProgressDetails(progress?.detailsJson);
    const nestedAnswers = parsedDetails.answers;

    if (nestedAnswers && typeof nestedAnswers === "object" && !Array.isArray(nestedAnswers)) {
        return nestedAnswers as Record<string, unknown>;
    }

    return Object.keys(parsedDetails).reduce((accumulator, key) => {
        if (["answers", "flowType", "lastSavedAt", "phase", "testing", "onboardingSnapshot", "testingEnabledAt"].includes(key)) {
            return accumulator;
        }

        accumulator[key] = parsedDetails[key];
        return accumulator;
    }, {} as Record<string, unknown>);
};
